import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import type { SowingInstruction } from "../../services/sowingInstructionsApi";
import { useApproveSowingInstruction } from "../../hooks/useSowingInstructions";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { SowingConfirmBody } from "./SowingConfirmSummary";

interface ApproveSowingDialogProps {
  instruction: SowingInstruction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const ApproveSowingDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: ApproveSowingDialogProps) => {
  const [notes, setNotes] = useState("");
  const approveMutation = useApproveSowingInstruction();
  const notify = useNotification();
  const confirm = useConfirm();

  const handleApprove = () => {
    confirm({
      title: "Approve sowing instruction?",
      description: "This instruction will be approved for sowing.",
      confirmLabel: "Approve",
      body: SowingConfirmBody(instruction, [
        { label: "Quantity to sow", value: `${instruction.quantityToSow}` },
      ]),
      onConfirm: () =>
        approveMutation
          .mutateAsync({
            sowingInstructionId: instruction.id,
            approved: true,
            notes,
          })
          .then(() => {
            setNotes("");
            notify.success("Sowing instruction approved successfully");
            onSuccess();
          })
          .catch((error: unknown) => {
            notify.error(getErrorMessage(error));
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve Sowing Instruction</DialogTitle>
          <DialogDescription>
            {instruction.variety} - {instruction.quantityToSow} units
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>Approval Notes</FieldLabel>
            <Textarea
              placeholder="Add any approval notes or conditions..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={approveMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={approveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? "Approving..." : "Approve"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};